import type { ScoUser } from '@src/graphql-types'

/** Membership SKUs that share one Chargebee subscription and can be prorate-changed. */
export const MEMBERSHIP_LADDER_PRODUCT_IDS = new Set([
  'scom-accelerator-basic',
  'scom-accelerator-tools',
  'scom-accelerator-community',
  'scom-accelerator-advisor',
])

type SubscriptionLike = {
  subscriptionId?: string | null
  isActive?: boolean | null
  cardLabel?: string | null
  productId?: string | null
  product?: {
    productId?: string | null
    termId?: string | null
    effectiveTermId?: string | null
    term?: { termId?: string | null } | null
  } | null
}

const STARTUPS_PRO_PRODUCT_ID = 'scom-accelerator-tools'
const ADVISOR_TEAM_PRODUCT_ID = 'scom-accelerator-advisor'

type UserLike = Pick<ScoUser, 'subscriptions'> | { subscriptions?: SubscriptionLike[] | null }

export function subscriptionProductId(s: SubscriptionLike) {
  return s.productId || s.product?.productId || null
}

/** Term the subscription is billed on. Null when the row does not say. */
export function subscriptionTermId(subscription: SubscriptionLike): string | null {
  return (
    subscription.product?.effectiveTermId ||
    subscription.product?.termId ||
    subscription.product?.term?.termId ||
    null
  )
}

/**
 * Term to compare with the plans on screen, for products the member holds.
 * Omitted when they do not hold it. `null` when they hold it but the term is
 * unknown — callers keep that card as the current plan so we do not sell a
 * second copy. When one subscription is already on `viewedTermId`, that term
 * is the value, so the card for it stays current.
 */
export function heldTermByProductId(
  user: UserLike | null | undefined,
  productIds: readonly string[],
  viewedTermId?: string | null
): Record<string, string | null> {
  const wanted = new Set(productIds.filter(Boolean))
  const byProduct = new Map<string, Array<string | null>>()
  for (const subscription of (user?.subscriptions || []) as SubscriptionLike[]) {
    if (!subscription?.isActive) {
      continue
    }
    const productId = subscriptionProductId(subscription)
    if (!productId || !wanted.has(productId)) {
      continue
    }
    const terms = byProduct.get(productId) || []
    terms.push(subscriptionTermId(subscription))
    byProduct.set(productId, terms)
  }

  const held: Record<string, string | null> = {}
  for (const [productId, terms] of byProduct) {
    if (viewedTermId && terms.includes(viewedTermId)) {
      held[productId] = viewedTermId
      continue
    }
    if (terms.some(term => !term)) {
      held[productId] = null
      continue
    }
    held[productId] = terms.find(term => term && term !== viewedTermId) || terms[0] || null
  }
  return held
}

/**
 * True when this product at this term is already what they pay for. Advisor Team
 * covers Startups Pro at any term. An unknown term counts as a match.
 */
export function membershipAlreadyCovers(
  user: UserLike | null | undefined,
  productId: string | null | undefined,
  termId?: string | null
) {
  if (!productId) {
    return false
  }
  const activeIds = ((user?.subscriptions || []) as SubscriptionLike[])
    .filter(subscription => subscription?.isActive)
    .map(subscriptionProductId)
    .filter((id): id is string => Boolean(id))
  if (productId === STARTUPS_PRO_PRODUCT_ID && activeIds.includes(ADVISOR_TEAM_PRODUCT_ID)) {
    return true
  }
  if (!activeIds.includes(productId)) {
    return false
  }
  const held = heldTermByProductId(user, [productId], termId)[productId]
  if (held == null || !termId) {
    return true
  }
  return held === termId
}

/**
 * Active membership-ladder subscriptions with a card on file: the only subscriptions a prorated
 * product change may target. A change replaces the subscription's items, so offering a
 * non-ladder subscription (Startups Unlimited, investor matching, ...) would silently swap that
 * plan out for the membership.
 */
export function getUpgradeableSubscriptions<T extends SubscriptionLike>(
  user?: { subscriptions?: Array<T | null> | null } | null
): T[] {
  return (user?.subscriptions || []).filter((s): s is T => {
    if (!s?.isActive || !s.cardLabel) {
      return false
    }
    const id = subscriptionProductId(s)
    return Boolean(id && MEMBERSHIP_LADDER_PRODUCT_IDS.has(id))
  })
}

/**
 * True when the user should change an existing membership instead of starting a new subscription.
 * Product moves (Startups Pro → Advisor Team) and a different billing term on the same product.
 */
export function canProrateUpgradeToProduct(
  user: UserLike | null | undefined,
  targetProductId: string | null | undefined,
  targetTermId?: string | null
) {
  if (!targetProductId || !MEMBERSHIP_LADDER_PRODUCT_IDS.has(targetProductId)) {
    return false
  }
  const upgradeable = getUpgradeableSubscriptions(user as { subscriptions?: SubscriptionLike[] })
  if (upgradeable.length === 0) {
    return false
  }
  if (membershipAlreadyCovers(user, targetProductId, targetTermId)) {
    return false
  }
  const activeProductIds = ((user?.subscriptions || []) as SubscriptionLike[])
    .filter(subscription => subscription?.isActive)
    .map(subscriptionProductId)
  if (!activeProductIds.includes(targetProductId)) {
    return true
  }
  // Same product, different term: only when that subscription can be changed in place.
  return upgradeable.some(subscription => {
    if (subscriptionProductId(subscription) !== targetProductId) {
      return false
    }
    const held = subscriptionTermId(subscription)
    return Boolean(targetTermId && held && held !== targetTermId)
  })
}

/**
 * Prefer the subscription whose term is actually changing. Otherwise prefer Startups Pro
 * when moving to Advisor Team.
 */
export function preferredUpgradeSubscriptionId(
  user: UserLike | null | undefined,
  targetProductId: string | null | undefined,
  targetTermId?: string | null
) {
  const upgradeable = getUpgradeableSubscriptions(user as { subscriptions?: SubscriptionLike[] })
  if (targetTermId) {
    const termChange = upgradeable.find(subscription => {
      if (subscriptionProductId(subscription) !== targetProductId) {
        return false
      }
      const held = subscriptionTermId(subscription)
      return Boolean(held && held !== targetTermId)
    })
    if (termChange?.subscriptionId) {
      return termChange.subscriptionId
    }
  }
  if (targetProductId === ADVISOR_TEAM_PRODUCT_ID) {
    const tools = upgradeable.find(
      subscription => subscriptionProductId(subscription) === STARTUPS_PRO_PRODUCT_ID
    )
    if (tools?.subscriptionId) {
      return tools.subscriptionId
    }
  }
  return upgradeable[0]?.subscriptionId || null
}
